/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-15 17:58:17
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import ReduxActions from 'redux-form/lib/actions';

import { FormLayout, Link } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderAutocompleteV2 from 'src/components/views/common/autocomplete-hoc-v2';
import {
  FILTER_OPTION_DISPLAY_TYPES_TAG,
  FILTER_OPTION_SELECT_TYPES,
  FILTER_OPTION_SWATCH_STYLES,
  getFilterOptionValueTypes
} from 'src/constants/filter-tree';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { hasTag } from 'src/helpers/form-validation-helper';
import { routes } from 'src/routes';
import { getShop } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = {
  tags: Array<Object>,
  optionType: string,
  tagOptionValues: string,
  tagDisplayType: string,
  tagPrefix: string,
  tagPrefixValue: string,
  tagManualValues: Array<Object>,
  tagExcludedValues: Array<Object>,
  themeSetupV2: boolean,
  host: string,
  appLanguage: string
};

type ComposedProps = Props & WithI18nProps;

class Tag extends Component<ComposedProps> {
  render() {
    const { i18n } = this.props;
    const displayTypes = FILTER_OPTION_DISPLAY_TYPES_TAG.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });
    const optionSelect = FILTER_OPTION_SELECT_TYPES.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });
    const swatchStyles = FILTER_OPTION_SWATCH_STYLES.map(function (style) {
      return {
        ...style,
        label: i18n.translate(style.label)
      };
    });

    const {
      tagDisplayType,
      optionType,
      tagOptionValues,
      tagPrefix,
      tagPrefixValue,
      themeSetupV2,
      host,
      appLanguage
    } = this.props;
    const optionValues = getFilterOptionValueTypes(optionType, appLanguage, i18n);

    let { tagManualValues, tagExcludedValues } = this.props;
    const shop = getShop();

    return (
      <FormLayout>
        <Field
          name="tagDisplayType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Label'
          )}
          options={displayTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Tooltip'
          )}
          helperText={
            tagDisplayType === 'swatch' &&
            (themeSetupV2 ? (
              <span>
                <span style={{ color: '#DE3618', fontWeight: 500 }}>
                  {i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText_ImportantNOTE'
                  )}
                </span>{' '}
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText1_Content',
                  {
                    t: (
                      <Link
                        external
                        url={`${routes.swatchSettings.view()}?shop=${shop}&host=${host}`}>
                        {i18n.translate(
                          'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText1_Content_t'
                        )}
                      </Link>
                    )
                  }
                )}
              </span>
            ) : (
              <span>
                <span style={{ color: '#DE3618', fontWeight: 500 }}>
                  {i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText_ImportantNOTE'
                  )}
                </span>{' '}
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText2_Content',
                  {
                    t: (
                      <Link external url={URL.OptionTypeSwatchDoc}>
                        {i18n.translate(
                          'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText2_Content_t'
                        )}
                      </Link>
                    )
                  }
                )}
              </span>
            ))
          }
        />

        {tagDisplayType === 'swatch' && themeSetupV2 && (
          <Field
            name="tagSwatchStyle"
            component={RenderSelect}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SwatchStyle_Label'
            )}
            options={swatchStyles}
          />
        )}

        <Field
          name="tagOptionSelect"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Label'
          )}
          options={optionSelect}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Tooltip'
          )}
        />
        <Field
          name="tagOptionValues"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Label'
          )}
          options={optionValues}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Tooltip'
          )}
        />

        <Field
          name="tagPrefix"
          component={RenderCheckbox}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_UsingPrefix_Label'
          )}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_UsingPrefix_Tooltip'
          )}
          onChange={this.resetPrefix}
        />
        {tagPrefix ? (
          <Field
            name="tagPrefixValue"
            component={RenderTextField}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_FilterTheTagsByAPrefix_Label'
            )}
            onChange={this.resetFieldValues}
            tooltip={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_FilterTheTagsByAPrefix_Tooltip'
            )}
            validate={[hasTag]}
          />
        ) : (
          ''
        )}
        {tagOptionValues === 'specific' ? (
          <Field
            name="tagManualValues"
            component={RenderAutocompleteV2}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ManualValues_Label'
            )}
            value={tagManualValues}
            type={'tags'}
            filterPrefix={tagPrefixValue}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SelectThreeDots'
            )}
          />
        ) : (
          ''
        )}

        {tagOptionValues !== 'specific' ? (
          <div className="BC-Autocomplete__Wrapper">
            {tagPrefix && _.isEmpty(tagPrefixValue) && (
              <span className="BC-Autocomplete__Backdrop" onClick={this.confirmTagPrefix}></span>
            )}
            <Field
              name="tagExcludedValues"
              component={RenderAutocompleteV2}
              label={i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ExcludedValues_Label'
              )}
              value={tagExcludedValues}
              type={'tags'}
              filterPrefix={tagPrefixValue}
              placeholder={i18n.translate(
                'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SelectThreeDots'
              )}
            />
          </div>
        ) : (
          ''
        )}
      </FormLayout>
    );
  }

  resetFieldValues = value => {
    const { updateManualValue, tagManualValues, updateExcludedValues, tagExcludedValues } =
      this.props;

    if (!_.isEmpty(tagManualValues)) {
      updateManualValue();
    }
    if (!_.isEmpty(tagExcludedValues)) {
      updateExcludedValues();
    }
  };

  resetPrefix = checked => {
    if (!checked) {
      this.props.clearPrefix();
    }
  };

  confirmTagPrefix = () => {
    const { i18n, updateFieldError, touch } = this.props;
    touch('tagPrefixValue');
    updateFieldError(
      'tagPrefixValue',
      i18n.translate('AdminHTML.FilterTree.Item.optionTable_ConfirmTagPrefix_Content')
    );
  };
}

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    optionType: _.get(formFilterOptionValues, 'optionType', ''),
    tagOptionValues: _.get(formFilterOptionValues, 'tagOptionValues', ''),
    tagDisplayType: _.get(formFilterOptionValues, 'tagDisplayType', ''),
    tagPrefix: _.get(formFilterOptionValues, 'tagPrefix', ''),
    tagPrefixValue: _.get(formFilterOptionValues, 'tagPrefixValue', ''),
    tagManualValues: _.get(formFilterOptionValues, 'tagManualValues', []),
    tagExcludedValues: _.get(formFilterOptionValues, 'tagExcludedValues', []),
    themeSetupV2: _.get(tenantApp, 'adminAppFeatures.themeSetupV2', false),
    host: _.get(state.oauth, 'host', ''),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

const mapDispatchToProps = dispatch => ({
  updateManualValue: () => dispatch(change(`OptionModalForm`, `tagManualValues`, [])),
  updateExcludedValues: () => dispatch(change(`OptionModalForm`, `tagExcludedValues`, [])),
  clearPrefix: () => dispatch(change(`OptionModalForm`, `tagPrefixValue`, '')),
  updateField: (field, value) => dispatch(change(`OptionModalForm`, field, value)),
  updateFieldError: (field, message) =>
    dispatch(ReduxActions.updateSyncErrors(`OptionModalForm`, { [field]: message }))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(Tag));
